// Step 5 — Review & Submit
import React, { useState } from 'react';
import { FileDown } from 'lucide-react';
import type { POAFormState } from '../../../types/poa';
import { DESIGNATION_CODES } from '../poaConstants';

interface Props {
  form: POAFormState;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 13, marginBottom: 6 }}>
      <span style={{ color: 'var(--muted)', minWidth: 200, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function maskSsn(ssn: string): string {
  // Show only last 4 digits in the review screen
  if (!ssn) return '';
  const digits = ssn.replace(/\D/g, '');
  return `XXX-XX-${digits.slice(-4)}`;
}

function formatPeriod(m: POAFormState['taxMatters'][0]): string {
  if (m.periodType === 'year')     return String(m.periodStart);
  if (m.periodType === 'range')    return `${m.periodStart} – ${m.periodEnd}`;
  return m.periodRaw;
}

export default function ReviewStep({ form, submitting, onSubmit, onBack }: Props) {
  const [downloading, setDownloading] = useState(false);
  const desLabel = (code: string) =>
    DESIGNATION_CODES.find((d) => d.value === code)?.label ?? code;

  async function handlePreviewPdf() {
    setDownloading(true);
    try {
      const res = await fetch('/api/poa-pdf', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'PDF generation failed.' }));
        alert(err.error || 'PDF generation failed.');
        return;
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const name = (form.taxpayerName || 'poa').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 40);
      a.download = `Form_2848_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <div style={{ background: 'rgba(122,162,255,.08)', border: '1px solid rgba(122,162,255,.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
        Please review all information carefully. Once you click <strong>Create POA</strong>, the record
        will be saved to the database. Signatures will be collected in the next step via SafeSend.
      </div>

      <div className="card" style={{ padding: 20 }}>
        <Section title="Part I — Taxpayer Information">
          <Row label="Name" value={form.taxpayerName} />
          <Row label="Address" value={form.taxpayerAddress} />
          {form.taxpayerEin  && <Row label="EIN"   value={form.taxpayerEin} />}
          {form.taxpayerSsn  && <Row label="SSN"   value={maskSsn(form.taxpayerSsn)} />}
          {form.taxpayerPhone      && <Row label="Phone"       value={form.taxpayerPhone} />}
          {form.taxpayerEmail      && <Row label="Email"       value={form.taxpayerEmail} />}
          {form.taxpayerPlanNumber && <Row label="Plan Number" value={form.taxpayerPlanNumber} />}
        </Section>

        <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />

        <Section title="Part I — Representatives">
          {form.representatives.map((rep, i) => (
            <div key={rep.id} style={{ marginBottom: i < form.representatives.length - 1 ? 16 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Rep #{i + 1}</div>
              <Row label="Name"              value={rep.name} />
              <Row label="Address"           value={rep.address} />
              <Row label="CAF Number"        value={rep.cafNumber || 'None'} />
              <Row label="PTIN"              value={rep.ptin} />
              <Row label="Phone"             value={rep.phone} />
              <Row label="Fax"               value={rep.fax} />
              <Row label="Receives Notices"  value={rep.receivesNotices ? 'Yes' : 'No'} />
              <Row label="Designation"       value={desLabel(rep.designationCode)} />
              <Row label="Jurisdiction"      value={rep.licensingJurisdiction} />
              <Row label="License Number"    value={rep.licenseNumber} />
            </div>
          ))}
        </Section>

        <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />

        <Section title="Part I — Tax Matters (Line 3)">
          {form.taxMatters.map((m, i) => (
            <div key={m.id} style={{ marginBottom: i < form.taxMatters.length - 1 ? 12 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Matter #{i + 1}</div>
              <Row label="Description"  value={m.matterDescription} />
              <Row label="Form Number"  value={m.taxFormNumber} />
              <Row label="Period"       value={formatPeriod(m)} />
            </div>
          ))}
        </Section>

        {(form.isSpecificUseNotOnCaf || form.authIntermediateServiceProvider ||
          form.authDisclosureThirdParties || form.authSubstituteAddRep || form.authSignReturn ||
          form.authOtherActs || form.unauthOtherDeletions || form.retainPriorPoa) && (
          <>
            <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />
            <Section title="Lines 4–6 — Acts & Limits">
              {form.isSpecificUseNotOnCaf && <Row label="Line 4" value="Specific use NOT recorded on CAF" />}
              {form.authIntermediateServiceProvider && <Row label="Line 5a" value="Access IRS records via Intermediate Service Provider" />}
              {form.authDisclosureThirdParties      && <Row label="Line 5a" value="Authorize disclosure to third parties" />}
              {form.authSubstituteAddRep            && <Row label="Line 5a" value="Substitute or add representative" />}
              {form.authSignReturn                  && <Row label="Line 5a" value="Sign a return" />}
              {form.authOtherActs                   && <Row label="Line 5a — Other" value={form.authOtherActs} />}
              {form.unauthOtherDeletions            && <Row label="Line 5b — Restrictions" value={form.unauthOtherDeletions} />}
              {form.retainPriorPoa                  && <Row label="Line 6" value="Retain prior POA(s) on file" />}
            </Section>
          </>
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', marginBottom: 20 }}>
        SSN is masked for display. The full SSN will be stored securely in the database.
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onBack} disabled={submitting || downloading} style={{ fontSize: 14 }}>
          Back — Edit
        </button>
        <button
          className="btn"
          onClick={handlePreviewPdf}
          disabled={submitting || downloading}
          style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, opacity: downloading ? .6 : 1 }}
        >
          <FileDown size={14} />
          {downloading ? 'Generating…' : 'Preview PDF'}
        </button>
        <button
          className="btn"
          onClick={onSubmit}
          disabled={submitting || downloading}
          style={{
            fontSize: 14, fontWeight: 600,
            background: 'rgba(122,162,255,.15)',
            borderColor: 'rgba(122,162,255,.5)',
            color: 'var(--accent)',
            opacity: submitting ? .6 : 1,
          }}
        >
          {submitting ? 'Saving…' : 'Create POA'}
        </button>
      </div>
    </div>
  );
}
